import { EntityList, renderDate } from 'components/EntityList'
import { FlexSpinner } from 'components/FlexSpinner'
import { FieldProps } from 'formik'
import { omitDeep } from 'helpers'
import { useCurrentProving } from 'hooks/useCurrentProving'
import useSelection from 'hooks/useSelection'
import { chain } from 'lodash'
import { noop } from 'lodash-es'
import { UnionVehicle, VehicleType } from 'model'
import { MarqueeSelection, SelectionMode } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'
import { generateVehicleGroups } from './helpers'
import { useProvingVehiclesQuery } from './VehicleListPicker.generated'

export const FormikVehicleByProvingPicker: React.FC<FieldProps<any>> = ({
  field,
  form,
}) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const { data, loading, error } = useProvingVehiclesQuery({
    variables: { filter: getFilter(proving.id) },
  })

  const provings = data && !loading ? data.provings : []

  const vehiclesByProving = provings.map((prov) => {
    return {
      id: prov.id,
      title: prov.title,
      models: chain(prov.vehicles)
        .forEach((v) => {
          if (!v.model) {
            v.model = ''
          }
        })
        .groupBy('model')
        .map((vehicles, model) => ({ id: model || '', vehicles }))
        .value(),
    }
  })

  const { items, groups } = generateVehicleGroups(vehiclesByProving as any, t)

  const [selection] = useSelection<UnionVehicle>({
    onSelectionChanged(selectedItems) {
      form.setFieldValue(
        field.name,
        omitDeep(selectedItems, ['__typename', 'orderId'])
      )
    },
    getKey: (item) => item._id,
  })

  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])

  const columns = [
    fieldColumn('vehicleLabel', {
      onRender: (item) => {
        if (item.unknown) {
          return t('model.proving.vehicle.vehicleUnknown.long')
        }
        if (item.type === VehicleType.Internal) {
          return item.label
        } else {
          return item.labelExternal
        }
      },
    }),
    fieldColumn('licensePlate'),
    fieldColumn('dateFrom', { onRender: renderDate }),
    fieldColumn('dateTo', { onRender: renderDate }),
  ]

  return (
    <FlexSpinner loading={loading} error={error}>
      <MarqueeSelection selection={selection}>
        <EntityList
          items={items}
          groups={groups}
          columns={columns}
          compact={true}
          selection={selection}
          selectionMode={SelectionMode.multiple}
          selectionPreservedOnEmptyClick={true}
          onColumnHeaderClick={() => {
            return noop
          }}
          onRenderRow={(props, defaultRender?) => (
            <div data-selection-toggle="true">
              {defaultRender && defaultRender(props)}
            </div>
          )}
          disableListStyleToggle={true}
        />
      </MarqueeSelection>
    </FlexSpinner>
  )
}

function getFilter(provingToExclude?: string, dateFrom?: Date, dateTo?: Date) {
  let filter = {}
  if (dateFrom && dateTo) {
    filter = { dateFrom: { GTE: dateFrom }, dateTo: { LTE: dateTo } }
  } else if (dateFrom) {
    filter = { dateFrom: { GTE: dateFrom } }
  } else if (dateTo) {
    filter = { dateFrom: { LTE: dateTo } }
  }

  if (provingToExclude) {
    filter = {
      ...filter,
      id: { NE: provingToExclude },
    }
  }

  return filter
}
